<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useWindowStore } from '@/stores/window';

    const props = defineProps({
        headline: {
            type: String,
            required: false,
            default: 'thank you to our 2026 sponsors!',
        },
        year: {
            type: Number,
            required: false,
            default: 2026,
        },
    });

    // Initiate Stores
    const { scrollY, innerHeight } = storeToRefs(useWindowStore());

    const { data: sponsors } = await useAsyncData(
        `sponsors-${props.year}`,
        () => $fetch('/api/v1/sponsors', { query: { year: props.year } }),
        { default: () => [], watch: [() => props.year] },
    );
    const now = useState('now', () => {
        return new Date().getTime();
    });

    const formattedSponsors = computed(() => {
      const formatted = {};

        const sponsorLevels = ['PREMIUM', 'VISIONARY', 'ELEVATED', 'MEMORABLE+', 'MEMORABLE', 'FOUNDATIONAL', 'EMERGING'];

        sponsorLevels.forEach((level) => {
            formatted[level.toLowerCase()] = [];
        });

        sponsors.value.forEach((sponsor) => {
            if (!sponsor.level) return;
            // Skip sponsors that have no logo — they can't render in the list,
            // and including them in the bucket inflates the section count.
            if (!sponsor.sponsor_logo?.length) return;
            const level = sponsor.level.toLowerCase();
            if (!formatted[level]) formatted[level] = [];
            formatted[level].push(sponsor);
        });

        Object.keys(formatted).forEach((key) => {
            if (!formatted[key].length) {
                delete formatted[key];
            } else {
                sections.value.push(key);
            }
        });

        return formatted;
    });

    const sections = ref([]);

    const countObject = computed(() => {
        if (!formattedSponsors) return false;
        const result = {};
        const formattedKeys = Object.keys(formattedSponsors.value);
        formattedKeys.forEach((key) => {
            result[key] = formattedSponsors.value[key].length;
        });
        return result;
    });

    const activeLevel = ref(Object.keys(formattedSponsors.value)[0]);

    const scrollHashMap = () => {
        if (process.server) return;
        const keys = Object.keys(formattedSponsors.value);
        const hashMap = {};
        if (!keys.length) return {};
        keys.forEach((key) => {
            const element = document.getElementById(key.toLowerCase());
            if (!element) return;
            const rect = element.getBoundingClientRect();
            const offset = rect.top;
            hashMap[key] = offset;
        });
        return hashMap;
    };

    function handleUpdate(value) {
        activeLevel.value = value;
    }

    watch(scrollY, () => {
        if (process.server) return;
        const hashMap = scrollHashMap();
        const entries = Object.entries(hashMap);
        if (entries.length === 0) return;

        const weights = {};
        entries.forEach(([key, value]) => {
            let weight = value < innerHeight.value - innerHeight.value / 2 ? 1 : 0;
            if (weight) weights[key] = weight;
        });
        const keys = Object.keys(weights);
        const index = keys.length - 1;
        activeLevel.value = index < 0 ? entries[0][0] : keys[index];
    });

    const jumpTo = (section) => {
        if (process.server) return;
    };
</script>

<template>
    <section class="block-sponsors relative overflow-clip bg-pink py-[43px] lg:py-[60px]" :data-refesh="now">
        <div class="px-(--box-padding-lr-m) lg:px-(--box-padding-lr) relative z-[1] text-(--magenta)">
            <MessHtml
                :html="headline"
                tag="h2"
                class="font-hubspot-serif font-normal text-[calc(42/var(--base-fs)*1rem)] leading-[1.1] tracking-normal lg:text-[calc(60/var(--base-fs)*1rem)] mb-0 lg:mb-[1.5rem]" />
            <FragmentsGenericStickyBar :sections="sections" :activeSection="activeLevel" :countObject="countObject" @jumpTo="jumpTo" :noContainer="true" />
            <FragmentsSponsorsList :sponsors="formattedSponsors" />
        </div>
    </section>
</template>

<style>
    /* no orbs — clean pink background */
</style>
